import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchPublishedBlog, recordBlogRead } from '../blog/publicBlogApi';
import { formatLongDate } from '../blog/blogForm';
import { absoluteHttps, siteOrigin, usePageMeta } from '../blog/usePageMeta';
import { PublicFooter, PublicNav } from '../components/site/PublicChrome';
import { useWhatsAppLinks, WhatsAppCommunityLinks } from '../components/blog/WhatsAppCommunityLinks';

function Prose({ text }) {
  const value = String(text || '').trim();
  if (!value) return null;
  return value.split(/\n{2,}/).map((paragraph, index) => (
    <p key={index} style={{ whiteSpace: 'pre-wrap', margin: '0 0 16px', color: '#333', lineHeight: 1.75 }}>
      {paragraph}
    </p>
  ));
}

function Section({ title, children }) {
  if (!children) return null;
  return (
    <section style={{ marginTop: '36px' }}>
      <h2 style={headingStyle}>{title}</h2>
      {children}
    </section>
  );
}

function Subheading({ children }) {
  return <h3 style={{ fontSize: '1.05rem', color: '#3d3424', margin: '18px 0 8px' }}>{children}</h3>;
}

const headingStyle = {
  fontSize: '1.35rem',
  color: '#1d1d1d',
  margin: '0 0 12px',
  paddingBottom: '8px',
  borderBottom: '2px solid #eadfcb',
};

function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [readInfo, setReadInfo] = useState(null);
  const communityLinks = useWhatsAppLinks();

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setError('');
    setBlog(null);
    fetchPublishedBlog(slug)
      .then((result) => {
        if (!active) return;
        if (!result || result.status === 'draft') {
          setStatus('missing');
          return;
        }
        setBlog(result);
        setStatus('ready');
      })
      .catch((loadError) => {
        if (!active) return;
        setError(loadError.message || 'Unable to load this case study.');
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (status !== 'ready' || !blog?.slug) return undefined;
    let cancelled = false;
    const seenKey = `brlc_blog_read_${blog.slug}`;
    const alreadyCounted = window.sessionStorage.getItem(seenKey) === '1';
    if (alreadyCounted && Number.isFinite(Number(blog.read_count))) {
      setReadInfo({
        read_count: Number(blog.read_count) || 0,
        show_read_count: blog.show_read_count === true || blog.show_read_count === 1 || blog.show_read_count === '1',
      });
      return undefined;
    }
    recordBlogRead(blog.slug).then((result) => {
      if (cancelled || !result) return;
      window.sessionStorage.setItem(seenKey, '1');
      setReadInfo(result);
    });
    return () => {
      cancelled = true;
    };
  }, [status, blog]);

  const origin = siteOrigin();
  const titleText = blog ? (blog.meta_title || blog.title) : 'Case study';
  const description = blog ? (blog.meta_description || blog.short_summary || '') : 'Published case study from BLRC.';
  const canonical = blog?.slug ? `${origin}/blog/${blog.slug}` : `${origin}/blog`;
  const published = blog?.published_at ? String(blog.published_at).replace(' ', 'T') : undefined;
  const jsonLd = blog ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blog.title,
    description,
    datePublished: published,
    dateModified: blog.updated_at ? String(blog.updated_at).replace(' ', 'T') : published,
    image: absoluteHttps(blog.featured_image_url),
    mainEntityOfPage: canonical,
    author: { '@type': 'Organization', name: 'BLRC' },
    publisher: { '@type': 'Organization', name: 'BLRC' },
  }) : '';

  usePageMeta({
    title: `${titleText} | BLRC`,
    description,
    canonical,
    image: blog?.featured_image_url || '',
    imageAlt: blog?.image_alt || blog?.title || '',
    type: 'article',
    jsonLd,
  });

  const provisions = Array.isArray(blog?.provisions) ? blog.provisions : [];
  const groups = [
    ['constitutional', 'Constitutional Provisions'],
    ['statutory', 'Statutory Provisions'],
    ['rules', 'Rules and Regulations'],
  ].map(([type, label]) => ({
    label,
    items: provisions.filter((item) => item.provision_type === type && String(item.provision_text || '').trim()),
  })).filter((group) => group.items.length > 0);

  const facts = Array.isArray(blog?.facts) ? blog.facts.filter((item) => String(item.description || '').trim()) : [];
  const issues = Array.isArray(blog?.legal_issues) ? blog.legal_issues.filter((item) => String(item.issue_text || '').trim()) : [];
  const related = Array.isArray(blog?.related_cases) ? blog.related_cases.filter((item) => String(item.case_name || '').trim()) : [];
  const publishedLabel = formatLongDate(blog?.published_at);
  const decisionLabel = formatLongDate(blog?.decision_date);

  return (
    <>
      <PublicNav />
      <section style={{ background: 'linear-gradient(135deg, #a68947 0%, #634f23 100%)', paddingTop: '140px', paddingBottom: '48px', color: '#fff' }}>
        <div className="container">
          <Link to="/blog" style={{ color: '#fff', textDecoration: 'none', fontSize: '14px' }}>← All case studies</Link>
          {status === 'ready' && blog ? (
            <>
              <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.7rem)', lineHeight: 1.25, margin: '18px 0 12px', maxWidth: '860px', color: '#fff' }}>
                {blog.title}
              </h1>
              {publishedLabel ? (
                <time dateTime={String(blog.published_at).slice(0, 10)} style={{ opacity: 0.9, color: '#fff' }}>
                  Published {publishedLabel}
                </time>
              ) : null}
              {readInfo?.show_read_count ? (
                <p style={{ margin: '10px 0 0', color: '#fff', fontSize: '14px' }}>
                  {readInfo.read_count === 1 ? '1 reader' : `${readInfo.read_count} readers`}
                </p>
              ) : null}
            </>
          ) : (
            <h1 style={{ fontSize: '2rem', marginTop: '18px', color: '#fff' }}>Case study</h1>
          )}
        </div>
      </section>
      <article style={{ background: '#f7f4ee', padding: '40px 0 72px' }}>
        <div className="container">
          <div style={{ background: '#fff', borderRadius: '14px', padding: 'clamp(20px, 4vw, 40px)', boxShadow: '0 10px 30px rgba(48, 36, 14, 0.06)' }}>
            {status === 'loading' && <p role="status">Loading case study…</p>}
            {status === 'error' && (
              <div role="alert">
                <p style={{ color: '#7a2e2e' }}>{error}</p>
                <Link to="/blog">Return to case studies</Link>
              </div>
            )}
            {status === 'missing' && (
              <div>
                <h2 style={headingStyle}>This case study is not available</h2>
                <p>It may be unpublished or the address may be incorrect.</p>
                <Link to="/blog">Return to case studies</Link>
              </div>
            )}
            {status === 'ready' && blog && (
              <>
                <WhatsAppCommunityLinks links={communityLinks} variant="compact" />
                {blog.featured_image_url ? (
                  <img
                    src={blog.featured_image_url}
                    alt={blog.image_alt || blog.title}
                    style={{ width: '100%', maxHeight: '420px', objectFit: 'cover', borderRadius: '10px', marginBottom: '24px' }}
                  />
                ) : null}
                <p style={{ color: '#8a7340', fontWeight: 650, marginBottom: '8px' }}>
                  {[blog.court, blog.citation, decisionLabel ? `Decided ${decisionLabel}` : ''].filter(Boolean).join(' · ')}
                </p>
                {blog.short_summary ? (
                  <p style={{ fontSize: '1.08rem', lineHeight: 1.7, color: '#2b2b2b' }}>{blog.short_summary}</p>
                ) : null}

                {String(blog.introduction || '').trim() ? <Section title="Introduction"><Prose text={blog.introduction} /></Section> : null}
                {String(blog.brief_background || '').trim() ? <Section title="Brief Background"><Prose text={blog.brief_background} /></Section> : null}
                {String(blog.importance || '').trim() ? <Section title="Importance of the Case"><Prose text={blog.importance} /></Section> : null}
                {String(blog.relevant_facts || '').trim() ? <Section title="Relevant Facts"><Prose text={blog.relevant_facts} /></Section> : null}
                {String(blog.parties_involved || '').trim() ? <Section title="Parties Involved"><Prose text={blog.parties_involved} /></Section> : null}
                {String(blog.events_leading || '').trim() ? <Section title="Events Leading to the Dispute"><Prose text={blog.events_leading} /></Section> : null}
                {facts.length > 0 && (
                  <Section title="Chronological Events">
                    <ol style={{ paddingLeft: '18px', margin: 0 }}>
                      {facts.map((fact, index) => (
                        <li key={`${fact.event_date}-${index}`} style={{ marginBottom: '14px' }}>
                          <h3 style={{ fontSize: '1rem', margin: '0 0 4px' }}>{formatLongDate(fact.event_date) || 'Event'}</h3>
                          <Prose text={fact.description} />
                        </li>
                      ))}
                    </ol>
                  </Section>
                )}
                {issues.length > 0 && (
                  <Section title="Legal Issues">
                    <ol style={{ paddingLeft: '18px' }}>
                      {issues.map((issue, index) => (
                        <li key={index} style={{ marginBottom: '10px', lineHeight: 1.7 }}>{issue.issue_text}</li>
                      ))}
                    </ol>
                  </Section>
                )}
                {groups.length > 0 && (
                  <Section title="Legal Provisions">
                    {groups.map((group) => (
                      <div key={group.label}>
                        <Subheading>{group.label}</Subheading>
                        <ul>
                          {group.items.map((item, index) => (
                            <li key={index} style={{ marginBottom: '8px', lineHeight: 1.7 }}>{item.provision_text}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </Section>
                )}
                {(blog.petitioner_arguments || blog.respondent_arguments) && (
                  <Section title="Arguments of the Parties">
                    {blog.petitioner_arguments ? (<><Subheading>Petitioner / Appellant</Subheading><Prose text={blog.petitioner_arguments} /></>) : null}
                    {blog.respondent_arguments ? (<><Subheading>Respondent</Subheading><Prose text={blog.respondent_arguments} /></>) : null}
                  </Section>
                )}
                {(blog.final_decision || blog.relief_granted || blog.ratio_decidendi || blog.judicial_reasoning || blog.interpretation_of_law || blog.important_reasoning || blog.precedents || blog.impact) && (
                  <Section title="Judgment / Decision">
                    <>
                      {blog.final_decision ? (<><Subheading>Final Decision of the Court</Subheading><Prose text={blog.final_decision} /></>) : null}
                      {blog.relief_granted ? (<><Subheading>Relief Granted or Denied</Subheading><Prose text={blog.relief_granted} /></>) : null}
                      {blog.ratio_decidendi ? (<><Subheading>Ratio Decidendi</Subheading><Prose text={blog.ratio_decidendi} /></>) : null}
                      {blog.judicial_reasoning ? (<><Subheading>Judicial Reasoning / Analysis</Subheading><Prose text={blog.judicial_reasoning} /></>) : null}
                      {blog.interpretation_of_law ? (<><Subheading>Interpretation of Law</Subheading><Prose text={blog.interpretation_of_law} /></>) : null}
                      {blog.important_reasoning ? (<><Subheading>Important Reasoning Used by the Judges</Subheading><Prose text={blog.important_reasoning} /></>) : null}
                      {blog.precedents ? (<><Subheading>Precedents Relied Upon or Distinguished</Subheading><Prose text={blog.precedents} /></>) : null}
                      {blog.impact ? (<><Subheading>Impact of the Judgment</Subheading><Prose text={blog.impact} /></>) : null}
                    </>
                  </Section>
                )}
                {String(blog.conclusion || '').trim() ? <Section title="Conclusion"><Prose text={blog.conclusion} /></Section> : null}
                {related.length > 0 && (
                  <Section title="Related Case Laws">
                    <ul style={{ paddingLeft: '18px' }}>
                      {related.map((item, index) => (
                        <li key={index} style={{ marginBottom: '12px', lineHeight: 1.6 }}>
                          <h3 style={{ fontSize: '1rem', margin: '0 0 4px' }}>{item.case_name}</h3>
                          <p style={{ margin: 0, color: '#555' }}>
                            {[item.citation, item.court, item.year].filter(Boolean).join(' · ')}
                          </p>
                          {item.reference_url ? (
                            <a href={item.reference_url} target="_blank" rel="noopener noreferrer">Read the source</a>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}
                <WhatsAppCommunityLinks links={communityLinks} variant="cta" />
              </>
            )}
          </div>
        </div>
      </article>
      <PublicFooter />
    </>
  );
}

export default BlogDetail;
